import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Paper,
  Stack,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const scrollbarStyles = `
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 212, 255, 0.3);
  }
`;

export const TraderStrategyViewer = ({ 
  strategy = null, 
  open = true, 
  onClose = null, 
  apiBaseUrl: propApiBaseUrl = '/', 
  isPage = false 
}) => {
  const { strategyId } = useParams();
  const location = useLocation();
  const [loadedStrategy, setLoadedStrategy] = useState(strategy);
  const [loading, setLoading] = useState(!strategy && isPage);
  const [internalOpen, setInternalOpen] = useState(open);

  // Get data from navigation state if available
  const stateData = location.state || {};
  const apiBaseUrl = stateData.apiBaseUrl || propApiBaseUrl;
  const strategyFromState = stateData.strategy;

  // Use strategy from state if available, otherwise fetch
  useEffect(() => {
    if (strategyFromState) {
      setLoadedStrategy(strategyFromState);
      setLoading(false);
      return;
    }

    if (!strategy && strategyId && isPage) {
      setLoading(true);
      console.log('Loading strategy:', strategyId);
      
      fetch(`${apiBaseUrl}/strategies/api/bulk-strategy/status/${strategyId}/`)
        .then(res => res.json())
        .then(data => {
          console.log('Direct fetch response:', data);
          if (data.success && data.strategy) {
            setLoadedStrategy(data.strategy);
            setLoading(false);
          } else if (data.bulk_strategy_id) {
            setLoadedStrategy(data);
            setLoading(false);
          } else {
            throw new Error('Strategy not found in direct fetch');
          }
        })
        .catch(err => {
          console.log('Direct fetch failed, trying list endpoint:', err.message);
          fetch(`${apiBaseUrl}/strategies/api/bulk-strategy/list/`)
            .then(res => res.json())
            .then(data => {
              console.log('List fetch response:', data);
              if (data.success && data.bulk_strategies && Array.isArray(data.bulk_strategies)) {
                const foundStrategy = data.bulk_strategies.find(s => s.bulk_strategy_id === strategyId);
                if (foundStrategy) {
                  console.log('Found strategy in list:', foundStrategy);
                  setLoadedStrategy(foundStrategy);
                } else {
                  console.error('Strategy not found in list:', strategyId);
                }
              }
            })
            .catch(listErr => console.error('List fetch also failed:', listErr))
            .finally(() => setLoading(false));
        });
    }
  }, [strategyId, apiBaseUrl, strategy, isPage, strategyFromState]);

  const activeStrategy = loadedStrategy || strategy;
  const config = activeStrategy?.configuration || {};
  const state = activeStrategy?.current_state || {};
  const progress = activeStrategy?.progress || { completed: 0, total: 0 };
  const inPosition = state.in_position;

  const progressPercent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
  const entryTime = state.entry_time ? new Date(state.entry_time) : null;
  const timeRemaining = Math.max(
    0,
    Math.round(
      ((config.validity_window_minutes || 120) * 60 * 1000 - (Date.now() - (entryTime?.getTime() || Date.now()))) /
        1000 /
        60
    )
  );

  const getStatusIcon = (completed) => (completed ? <CheckCircleIcon /> : <UncheckedIcon />);

  const handleClose = () => {
    setInternalOpen(false);
    if (onClose) onClose();
  };

  const content = (
    <>
      {/* Quick Status Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha('#00d4ff', 0.1), border: `1px solid ${alpha('#00d4ff', 0.3)}` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                Symbol
              </Typography>
              <Typography variant="h5" sx={{ color: '#00d4ff', fontWeight: 700, mt: 0.5 }}>
                {activeStrategy?.symbol}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha('#ffa502', 0.1), border: `1px solid ${alpha('#ffa502', 0.3)}` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                Timeframe
              </Typography>
              <Typography variant="h5" sx={{ color: '#ffa502', fontWeight: 700, mt: 0.5 }}>
                {activeStrategy?.timeframe}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(inPosition ? '#00ff88' : '#ffd700', 0.1), border: `1px solid ${alpha(inPosition ? '#00ff88' : '#ffd700', 0.3)}` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                Status
              </Typography>
              <Typography variant="h5" sx={{ color: inPosition ? '#00ff88' : '#ffd700', fontWeight: 700, mt: 0.5 }}>
                {inPosition ? 'ACTIVE' : 'READY'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha('#764ba2', 0.1), border: `1px solid ${alpha('#764ba2', 0.3)}` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                Active
              </Typography>
              <Typography variant="h5" sx={{ color: activeStrategy?.is_active ? '#00ff88' : '#666', fontWeight: 700, mt: 0.5 }}>
                {activeStrategy?.is_active ? 'YES' : 'NO'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Section */}
      <Card sx={{ bgcolor: alpha('#000', 0.2), border: `1px solid ${alpha('#fff', 0.1)}`, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: '#00d4ff' }} />
            Trade Progress
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                Conditions Completed
              </Typography>
              <Typography variant="body2" sx={{ color: '#00d4ff', fontWeight: 600 }}>
                {progress.completed} of {progress.total}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: alpha('#000', 0.3),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
                },
              }}
            />
          </Box>

          {inPosition && (
            <Box sx={{ p: 2, bgcolor: alpha('#00ff88', 0.1), border: `1px solid ${alpha('#00ff88', 0.3)}`, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#00ff88' }}>
                <strong>Entry Time:</strong> {entryTime?.toLocaleTimeString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#00ff88', mt: 1 }}>
                <strong>Entry Price:</strong> ${state.entry_price?.toFixed(2) || '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ffa502', mt: 1 }}>
                <strong>Time Remaining:</strong> {timeRemaining}m
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Trade Flow */}
      <Card sx={{ bgcolor: alpha('#000', 0.2), border: `1px solid ${alpha('#fff', 0.1)}`, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon sx={{ color: '#ffd700' }} />
            How This Strategy Works
          </Typography>

          <Stack spacing={2}>
            {activeStrategy?.sub_strategies?.map((sub, idx) => {
              const isCompleted = state.completed_strategies?.[sub.strategy_id];
              return (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    bgcolor: alpha(isCompleted ? '#00ff88' : '#ffa502', 0.1),
                    border: `2px solid ${alpha(isCompleted ? '#00ff88' : '#ffa502', 0.3)}`,
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minW: 40 }}>
                      {getStatusIcon(isCompleted)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                        Step {idx + 1}: {sub.strategy_id}
                      </Typography>

                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase' }}>
                            Look For
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#00d4ff', fontFamily: 'monospace', fontWeight: 600 }}>
                            {sub.early_signal}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase' }}>
                            Confirm With
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#00ff88', fontFamily: 'monospace', fontWeight: 600 }}>
                            {sub.confirmation_signal}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase' }}>
                            Valid Window
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ffa502', fontWeight: 600 }}>
                            Between {sub.min_minutes}m - {sub.max_minutes}m
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Strategy Rules */}
      <Card sx={{ bgcolor: alpha('#000', 0.2), border: `1px solid ${alpha('#fff', 0.1)}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ color: '#764ba2' }} />
            Trade Rules
          </Typography>

          <List dense disablePadding>
            <ListItem disableGutters sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 32, color: '#00ff88' }}>
                <FlagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Entry Signal"
                secondary={`All ${config.required_strategies?.length || 0} conditions must be met`}
                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 600, fontSize: '0.9rem' } }}
                secondaryTypographyProps={{ sx: { color: alpha('#fff', 0.6), fontSize: '0.85rem' } }}
              />
            </ListItem>

            <ListItem disableGutters sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 32, color: '#ff4757' }}>
                <FlagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Exit Signal"
                secondary={config.exit_signal}
                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 600, fontSize: '0.9rem' } }}
                secondaryTypographyProps={{ sx: { color: '#ff4757', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.85rem' } }}
              />
            </ListItem>

            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32, color: '#ffd700' }}>
                <ScheduleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Validity Window"
                secondary={`${config.validity_window_minutes} minutes from first signal`}
                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 600, fontSize: '0.9rem' } }}
                secondaryTypographyProps={{ sx: { color: alpha('#fff', 0.6), fontSize: '0.85rem' } }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </>
  );

  // Loading state
  if (loading) {
    return (
      <Dialog open={internalOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: alpha('#1a1f2e', 0.95) }}>
          <CircularProgress sx={{ color: '#764ba2' }} />
        </DialogContent>
      </Dialog>
    );
  }

  if (!activeStrategy) {
    return (
      <Dialog open={internalOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ bgcolor: alpha('#1a1f2e', 0.95), color: '#fff' }}>
          <Typography>No strategy data available</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={internalOpen} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: alpha('#1a1f2e', 0.95),
          backgroundImage: 'none',
          border: `1px solid ${alpha('#00d4ff', 0.2)}`,
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: alpha('#1a1f2e', 0.9), color: '#00d4ff', fontWeight: 700, fontSize: '1.5rem', borderBottom: `1px solid ${alpha('#00d4ff', 0.2)}` }}>
        {activeStrategy.bulk_strategy_id}
      </DialogTitle>

      <DialogContent 
        sx={{ 
          bgcolor: alpha('#1a1f2e', 0.6), 
          color: '#fff', 
          p: 3,
          maxHeight: '70vh',
          ...scrollbarStyles
        }}
      >
        {content}
      </DialogContent>

      <DialogActions sx={{ bgcolor: alpha('#1a1f2e', 0.9), p: 2, borderTop: `1px solid ${alpha('#00d4ff', 0.2)}` }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            color: '#00d4ff', 
            fontWeight: 600,
            '&:hover': { bgcolor: alpha('#00d4ff', 0.1) } 
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            const text = JSON.stringify(activeStrategy, null, 2);
            navigator.clipboard.writeText(text);
          }}
          sx={{
            bgcolor: '#00d4ff',
            color: '#000',
            fontWeight: 700,
            '&:hover': { bgcolor: '#00a8cc' },
          }}
        >
          Copy Data
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TraderStrategyViewer;
